import {
    Text,
    View,
    Image,
    StyleSheet,
} from '@react-pdf/renderer';

import increaseIcon from "assets/img/report-arrow-increase.png";
import decreaseIcon from "assets/img/report-arrow-decrease.png";

import ReportPDFIndividualTable from './ReportPDFIndividualTable';
import ReportPDFEmptyChart from './ReportPDFEmptyChart';

import {
    ReportPDFIndividualCardInfo,
    ReportTimeRangeType,
    ReportItemSummaryType,
} from 'types/Reports';

import { getIndividualPeakDescriptions, getTitleByTime } from '../report/utils/reportString';
import { formatValueAndUnitBasedOnTypeInReport } from '../report/utils/reportFormat';

const individualUsageStyles = StyleSheet.create({
    individualCard: {
        position: 'relative',
        width: '100%',
        marginTop: 15,
        padding: 10,
        backgroundColor: '#F2F2F2',
    },
    individualCardTitle: {
        width: '100%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 500,
        color: '#333333',
    },
    individualCardComparisonContainer: {
        width: '100%',
        position: 'relative',
        fontFamily: 'Roboto',
        marginTop: 10,
    },
    individualCardComparison: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    individualCardSecondComparison: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: 5,
        marginBottom: 10,
    },
    individualCardComparisonPercentIcon: {
        position: 'relative',
        width: 16,
        height: 16,
    },
    individualCardComparisonValue: {
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 10,
        fontWeight: 400,
        color: '#333333',
        marginLeft: 3,
    },
    individualCardComparisonValueDescription: {
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 10,
        fontWeight: 400,
        color: '#636363',
        marginLeft: 3,
    },
});

type ReportPDFIndividualUsageCardProps = {
    title: string;
    timeRangeType: ReportTimeRangeType;
    itemType: ReportItemSummaryType;

    item: ReportPDFIndividualCardInfo;

    unit: string;

    color: string;

    reportPeriod: string;
};

const ReportPDFIndividualUsageCard = ({
    title,
    timeRangeType,
    itemType,
    item,
    unit,
    color,
    reportPeriod,
}: ReportPDFIndividualUsageCardProps) => {
    const {
        highestItemName,
        highestItemValue,
        lowestItemName,
        lowestItemValue,
        headers,
        rows,
    } = item;

    const titleTimeText = getTitleByTime(timeRangeType);

    let body: JSX.Element;

    if (rows.length === 0) {
        body = <ReportPDFEmptyChart />;
    } else {
        const maxValue = highestItemValue;
        const minValue = lowestItemValue;

        const highestItemValueDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, maxValue, unit, true);
        const lowestItemValueDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, minValue, unit, true);

        const { highest: highestDescription, lowest: lowestDescription } =
            getIndividualPeakDescriptions(itemType, reportPeriod);

        body = <>
            <View style={individualUsageStyles.individualCardComparison}>
                <Image style={individualUsageStyles.individualCardComparisonPercentIcon} src={increaseIcon} />
                <Text style={individualUsageStyles.individualCardComparisonValue}>{highestItemName}</Text>
                <Text style={individualUsageStyles.individualCardComparisonValueDescription}>
                    {`${highestDescription} ${highestItemValueDisplay}.*`}
                </Text>
            </View>

            {
                itemType !== 'waterLeakage' &&
                <View style={individualUsageStyles.individualCardSecondComparison}>
                    <Image style={individualUsageStyles.individualCardComparisonPercentIcon} src={decreaseIcon} />
                    <Text style={individualUsageStyles.individualCardComparisonValue}>{lowestItemName}</Text>
                    <Text style={individualUsageStyles.individualCardComparisonValueDescription}>
                        {`${lowestDescription} ${lowestItemValueDisplay}.*`}
                    </Text>
                </View>
            }

            {
                <ReportPDFIndividualTable
                    maxValue={maxValue}
                    minValue={minValue}
                    color={color}
                    unit={unit}
                    headers={headers}
                    rows={rows}
                    showAverage={itemType !== 'waterLeakage'}
                />
            }
        </>;
    }





    return (
        <View style={individualUsageStyles.individualCard}>
            <Text style={individualUsageStyles.individualCardTitle}>{title}{titleTimeText}</Text>
            <View style={individualUsageStyles.individualCardComparisonContainer}>
                {body}
            </View>
        </View>
    );
};

export default ReportPDFIndividualUsageCard;
